/**
 * The two rules for content cut off by the box that crops it: a shape against the `<svg>` it is
 * drawn in, and a line of copy against the HTML box holding it.
 *
 * The first of them:
 *
 * The rule for a shape cut off by the `<svg>` it is drawn in.
 *
 * An `<svg>` in an HTML document clips to its own box, and when the svg is sized to match its
 * `viewBox` that box is the viewBox. Geometry generated a little past it is therefore drawn and then
 * silently sliced: the defect that prompted this shipped a row of diamonds whose fifth column ran to
 * x=600 in a `viewBox="0 0 580 350"`, so it reached the frame with a flat vertical edge where its
 * right point should have been.
 *
 * Nothing else catches it. `content-overflows-canvas` measures against the canvas, and the `<svg>`
 * sits inside the canvas quite happily — it is the shape overflowing its own box, one level down.
 * The frame reviewer missed it too. An HTML container with `overflow: hidden` slices just as hard,
 * which is `text-clipped-by-container` below; this rule is about the svg boundary.
 *
 * Every question it asks is answered by a measurement or a resolved style, never by judgement about
 * what the author meant. What that leaves is a list of boxes that are not the shape a viewer sees —
 * an element that draws nothing, a shape the author already cropped itself, a glyph box that
 * overshoots its own ink — and each of those is skipped rather than guessed at. All the error is
 * pushed in the one direction that is cheap here: a missed slice costs a defect, and a false one
 * costs an authoring agent a blocking error it cannot clear.
 */
import { isContainerElement, settledFrame } from './geometryRules';
import { clippingAncestorsOf, describeElement, intersectRects } from './measure';
import type { ElementMeasurement, FrameSnapshot, Rect } from './measure';
import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';

const OPACITY_PRESENT_THRESHOLD = 0.05;

/**
 * How far past the edge is worth reporting. Sub-pixel slop from the svg's own scaling is not a
 * defect; the slice that prompted this rule was 20px, and a viewer reads a flat edge well below that.
 */
const CLIP_TOLERANCE_PX = 4;

/** An author's statement that this shape is meant to be cropped — a peek, a crop, a window. */
const ALLOW_CLIP_ATTR = 'data-l5-allow-clip';

/**
 * How much of a line has to reach the frame before its cropping is read as damage. Below this the
 * box is a window onto something longer, not a container the copy failed to fit.
 */
const VISIBLE_ENOUGH_TO_JUDGE = 0.25;

/**
 * Elements whose measured box is not the shape on screen, and so cannot be judged against an edge:
 * paint definitions and animation nodes that draw nothing at all; a `<foreignObject>`, whose
 * contents are clipped by the object rather than by the svg, so widening the svg would not move
 * them; and text, whose box is its font metrics — at 80px it hangs some 17px below the baseline
 * with no ink in it. Text is matched by ancestor rather than by the measurement's own `hasText`,
 * which counts only direct text-node children and so reads false for the `<text>` wrapping a
 * `<tspan>` — the element carrying the font box.
 */
const NOT_A_DRAWN_SHAPE = 'defs, clipPath, mask, symbol, pattern, marker, foreignObject, text';

/**
 * Whether this `<svg>` clips what overflows it.
 *
 * An inline `<svg>` is never the document root, so the UA stylesheet's `svg:not(:root) { overflow:
 * hidden }` applies and clipping is the default — which is why the absence of any declaration reads
 * as "clips" rather than as unknown. Only `visible` escapes: `auto` and `scroll` both clip in a
 * frame that cannot scroll, and Chrome rewrites the two-value `overflow: visible hidden` to
 * `auto hidden`, so treating `auto` as a bleed would silence the rule on a graphic it is cutting in
 * half. The resolved style is read first and the presentation attribute only as a fallback: an
 * `overflow` attribute is an author rule of specificity 0, so any stylesheet beats it, and reading
 * it first would call an svg a clipper that the cascade had already set to bleed.
 */
function clipsOverflow(svg: Element): boolean {
    const view = svg.ownerDocument?.defaultView;
    const declared = view?.getComputedStyle(svg).overflow || svg.getAttribute('overflow') || '';
    return !/^visible/i.test(declared.trim());
}

/** Every `<svg>` above this element that clips, innermost first. */
function clippingAncestors(element: Element): Element[] {
    const clippers: Element[] = [];
    let svg = element.parentElement?.closest('svg') ?? null;
    while (svg) {
        if (clipsOverflow(svg)) clippers.push(svg);
        svg = svg.parentElement?.closest('svg') ?? null;
    }
    return clippers;
}

/** How far `rect` reaches past `bounds` on its worst side, and which side that is. */
function worstOverflow(rect: Rect, bounds: Rect): { side: string; amount: number } {
    return [
        { side: 'left', amount: bounds.x - rect.x },
        { side: 'top', amount: bounds.y - rect.y },
        { side: 'right', amount: rect.x + rect.width - (bounds.x + bounds.width) },
        { side: 'bottom', amount: rect.y + rect.height - (bounds.y + bounds.height) },
    ].reduce((worst, candidate) => (candidate.amount > worst.amount ? candidate : worst));
}

/**
 * Whether the element's own box says nothing about what a viewer sees there.
 *
 * Measured in Chrome, all of these: an element that draws nothing — `<desc>`, `<linearGradient>`,
 * `<stop>`, `<filter>`, a `display: none` shape — reports a zero-size box at the page origin, which
 * against an svg placed anywhere but the corner reads as a shape hanging off the left edge by the
 * whole page offset, and no edit to the graphic could ever clear it. `visibility: hidden` keeps its
 * real box and a computed opacity of 1. A shape carrying its own `clip-path` or `mask` measures its
 * full geometry, so the author's own declared crop would be reported as an accident — read from the
 * resolved style, because these compositions declare it in a stylesheet as readily as on the element.
 */
export function boxIsNotTheShape(measured: ElementMeasurement): boolean {
    const { element, rect, opacity } = measured;
    if (rect.width <= 0 || rect.height <= 0) return true;
    if (opacity < OPACITY_PRESENT_THRESHOLD) return true;
    if (isContainerElement(element) || element.closest(NOT_A_DRAWN_SHAPE)) return true;
    if (isInsideDeclaredCrop(element)) return true;
    return element.ownerDocument?.defaultView?.getComputedStyle(element).visibility === 'hidden';
}

/**
 * Whether the author cropped this shape, on it or on anything holding it.
 *
 * `clip-path` and `mask` crop every descendant while computing to `none` on each of them, and the
 * skills teach exactly that shape — knockout-silhouette-icon draws its interior lines "deliberately
 * over-wide and clip[s] them to the boundary" on a wrapping `<g>`. Measured in Chrome: such a child
 * reports its full over-wide box, its own computed `clipPath` is `none`, and the crop is visible
 * only on the ancestor. The resolved style is what is read at each level, because these
 * compositions put the crop in a stylesheet as readily as on the element — an ancestor cropped by a
 * class satisfies neither an attribute selector nor its own computed style.
 */
function isInsideDeclaredCrop(element: Element): boolean {
    const view = element.ownerDocument?.defaultView;
    const cropped = (value?: string) => Boolean(value) && value !== 'none';
    for (let node: Element | null = element; node; node = node.parentElement) {
        if (node.tagName.toUpperCase() === 'BODY') break;
        const style = view?.getComputedStyle(node);
        if (cropped(style?.clipPath) || cropped(style?.mask)) return true;
        if (node.getAttribute('clip-path') || node.getAttribute('mask')) return true;
    }
    return false;
}

/**
 * The `data-l5-allow-clip` nearest this shape, and whether it is placed where it counts.
 *
 * It counts on the shape or on a group inside one of the boxes clipping it, never on a clipper
 * itself or above all of them: a hatch resolved by `closest()` that sits on the clipper would
 * silence the rule for every shape beneath it in one attribute, which is how the last one of these
 * got misused. A mark that does not count is reported rather than ignored — an author who marks,
 * re-lints and gets the identical error back has learnt only that the rule cannot be satisfied.
 */
function croppableMark(element: Element, clippers: Element[]): { honoured: boolean } | null {
    const marked = element.closest(`[${ALLOW_CLIP_ATTR}]`);
    if (!marked) return null;
    const onAClipper = clippers.includes(marked);
    const insideAClipper = clippers.some((clipper) => clipper.contains(marked));
    return { honoured: !onAClipper && insideAClipper };
}

/** How far the element hangs outside every box clipping it in this frame, or null if it cannot say. */
function overflowIn(
    frame: FrameSnapshot,
    element: Element,
    clippers: Element[],
): { side: string; amount: number; bounds: Rect; rect: Rect } | null {
    const measured = frame.elements.find((m) => m.element === element);
    if (!measured || boxIsNotTheShape(measured)) return null;
    let bounds: Rect | null = null;
    for (const clipper of clippers) {
        const box = frame.elements.find((m) => m.element === clipper)?.rect;
        if (!box) return null;
        bounds = bounds ? intersectRects(bounds, box) : box;
    }
    return bounds ? { ...worstOverflow(measured.rect, bounds), bounds, rect: measured.rect } : null;
}

export const shapeClippedByViewbox: RuntimeLintRule = {
    id: 'shape-clipped-by-viewbox',
    severity: 'error',
    guidance:
        'Keep every drawn shape inside the box of the `<svg>` holding it — its `viewBox`, when the ' +
        'svg is sized to match. An inline `<svg>` clips, so geometry generated past that box — the ' +
        'last column of a row laid out by index, a ring wider than the box it was authored for — is ' +
        'drawn with a flat edge where its point should be. Widen the svg or bring the geometry ' +
        'inside it; shapes meant to be cropped go in a `<g data-l5-allow-clip>` **inside** that svg, ' +
        'which is the one place the mark counts.',
    check(context: RuntimeLintContext): string[] {
        const frame = settledFrame(context);
        if (!frame) return [];

        const messages: string[] = [];
        for (const measured of frame.elements) {
            if (boxIsNotTheShape(measured)) continue;

            const clippers = clippingAncestors(measured.element);
            if (clippers.length === 0) continue;
            const mark = croppableMark(measured.element, clippers);
            if (mark?.honoured) continue;

            const settled = overflowIn(frame, measured.element, clippers);
            if (!settled || settled.amount <= CLIP_TOLERANCE_PX) continue;
            // An exit can still be in flight at the last probe, and a shape the viewer saw whole
            // earlier is not a defect. The earlier reading only counts if the shape was drawn at
            // least as large as it ends up: a scale-in entrance passes through every size on its
            // way up, so "it was inside once" would forgive every pop-in — which is most of them —
            // including the bar taller than its own plot area that this rule most needs to catch.
            const seenWhole = (context.frames ?? []).some((other) => {
                const there = overflowIn(other, measured.element, clippers);
                return (
                    there !== null &&
                    there.amount <= CLIP_TOLERANCE_PX &&
                    there.rect.width >= settled.rect.width - 1 &&
                    there.rect.height >= settled.rect.height - 1
                );
            });
            if (seenWhole) continue;

            const clipper = describeElement(clippers[0]);
            const wholly = settled.bounds.width <= 0 || settled.bounds.height <= 0;
            const where = wholly
                ? `falls entirely outside ${clipper}, which clips — so none of it reaches the frame`
                : `is drawn ${Math.round(settled.amount)}px on screen past the ${settled.side} ` +
                  `edge of ${clipper}, which clips — so it reaches the frame with a flat edge ` +
                  `there instead of its own shape`;
            const hatch = mark
                ? ` The ${ALLOW_CLIP_ATTR} on ${describeElement(measured.element.closest(`[${ALLOW_CLIP_ATTR}]`)!)} does not count: a mark on the clipping svg, or outside it, would silence every shape inside. Move it onto this shape or a <g> within ${clipper}.`
                : ` Wrap it in a <g ${ALLOW_CLIP_ATTR}> inside ${clipper} if the crop is deliberate.`;
            messages.push(
                `${describeElement(measured.element)} ${where}. Enlarge ${clipper} or bring the ` +
                    `geometry inside it — measure the change in the svg's own units, not in screen ` +
                    `pixels, when the two differ.${hatch}`,
            );
        }
        return messages;
    },
};

/**
 * Copy this rule has nothing to say about.
 *
 * `visibility: hidden` keeps its layout — measured in Chrome it still reports its line rects and a
 * computed opacity of 1 — and parking a label hidden inside a window is an ordinary pre-animation
 * pose, so reading it would block an author over copy nobody sees. And `text-overflow: ellipsis` is
 * a declaration that the copy is *meant* to be cut: the ellipsis is painted, while the range still
 * reports the whole untruncated line (measured: 671px of it inside a 200px box), so every correct
 * truncation would otherwise read as 471px of damage that no change to the box could fix.
 */
function isHiddenOrTruncated(element: Element): boolean {
    const style = element.ownerDocument?.defaultView?.getComputedStyle(element);
    if (!style) return false;
    return style.visibility === 'hidden' || style.textOverflow === 'ellipsis';
}

/**
 * Whether the window shows a sliver of a much longer run rather than a line that nearly fits.
 *
 * A wheel of digits parks thirty of them behind a one-digit window and slides them through, so
 * measuring what escapes reports the technique rather than a defect. Measured across the skills
 * that build windows: a digit strip shows 3% of its own ink, while a row whose descenders are
 * shaved shows 65%, and 40% when its copy has wrapped to a second line. The crawling ticker and
 * the scrolling feed turn out to need no exemption at all — every line of copy in both sits inside
 * its window once the scene has settled.
 *
 * Reading the proportion rather than the movement also keeps the common masked reveal in scope: it
 * animates the copy inside a static wrapper, so a test for movement would excuse it for good, while
 * at rest it has arrived and its ink either fits or is being cut.
 */
function showsOnlyASliver(glyph: Rect, clip: Rect): boolean {
    const ink = Math.max(0, glyph.width) * Math.max(0, glyph.height);
    if (ink <= 0) return true;
    const shown = intersectRects(glyph, clip);
    return (shown.width * shown.height) / ink < VISIBLE_ENOUGH_TO_JUDGE;
}

/**
 * Whether a crop is the frame itself rather than a window inside it.
 *
 * These compositions wrap everything in a full-bleed stage carrying `overflow: hidden`, so every
 * line of copy has a clipping ancestor and a rule that counted it would report the canvas edge a
 * second time. `content-overflows-canvas` owns that edge; this rule is about the boxes inside it.
 */
function holdsTheWholeCanvas(
    clip: Rect,
    canvas: { width: number; height: number } | undefined,
): boolean {
    if (!canvas) return false;
    return (
        clip.x <= CLIP_TOLERANCE_PX &&
        clip.y <= CLIP_TOLERANCE_PX &&
        clip.x + clip.width >= canvas.width - CLIP_TOLERANCE_PX &&
        clip.y + clip.height >= canvas.height - CLIP_TOLERANCE_PX
    );
}

/**
 * The rule for copy shaved by the HTML box that crops it.
 *
 * A line box is only as tall as its `line-height`; the glyphs inside it are drawn from the font's
 * own ascent and descent, which at `line-height: 1` is the taller of the two. So a row sized to the
 * font-size holds its line box exactly and still slices the descenders off its own copy — the
 * defect that prompted this shipped a 96px row of 96px type where the `p` in "One product" came out
 * flat-bottomed and the comma in "25,000" came out square.
 *
 * Nothing else catches it, in either direction. `content-overflows-canvas` measures against the
 * canvas, and the row sits inside the canvas quite happily. `shape-clipped-by-viewbox` is about the
 * svg boundary. A box-against-box check sees nothing at all here, because the line box fits its row
 * to the pixel — only the ink escapes, which is why this is the one rule that measures glyphs
 * rather than boxes.
 *
 * It reads the settled frame alone. Copy sliding up from behind a mask is half-hidden on its way in
 * and whole when it arrives, and a rule that fired mid-reveal would block every masked entrance the
 * skills teach.
 */
export const textClippedByContainer: RuntimeLintRule = {
    id: 'text-clipped-by-container',
    severity: 'error',
    guidance:
        'Give a box that crops (`overflow: hidden`) room for the ink of the type inside it. A line ' +
        "box is only as tall as its `line-height`, while glyphs are drawn from the font's own " +
        'ascent and descent, so a row sized to its font-size shaves the descenders off its own ' +
        'copy and a window built from a display `line-height` of 1 or less sits on the glyph. Size ' +
        'the crop from the type and leave clearance; copy meant to be cropped goes in an element ' +
        'carrying `data-l5-allow-clip` **inside** the box that crops it.',
    check(context: RuntimeLintContext): string[] {
        const frame = settledFrame(context);
        if (!frame) return [];

        const messages: string[] = [];
        for (const measured of frame.elements) {
            const { element, glyphRect, clipRect } = measured;
            if (!glyphRect || !clipRect) continue;
            if (measured.opacity < OPACITY_PRESENT_THRESHOLD) continue;
            if (isInsideDeclaredCrop(element)) continue;
            if (isHiddenOrTruncated(element)) continue;

            if (holdsTheWholeCanvas(clipRect, context.canvasSize)) continue;
            if (showsOnlyASliver(glyphRect, clipRect)) continue;

            const clippers = clippingAncestorsOf(element);
            const mark = croppableMark(element, clippers);
            if (mark?.honoured) continue;

            const { side, amount } = worstOverflow(glyphRect, clipRect);
            if (amount <= CLIP_TOLERANCE_PX) continue;

            const clipper = clippers[0] ? describeElement(clippers[0]) : 'the box above it';
            const hatch = mark
                ? ` The ${ALLOW_CLIP_ATTR} on ${describeElement(element.closest(`[${ALLOW_CLIP_ATTR}]`)!)} does not count: a mark on the cropping box, or outside it, would silence every line inside. Move it onto this copy or a wrapper within ${clipper}.`
                : ` Wrap it in an element carrying ${ALLOW_CLIP_ATTR} inside ${clipper} if the crop is deliberate.`;
            messages.push(
                `${describeElement(element)} has ${Math.round(amount)}px of its glyphs cut off the ` +
                    `${side} by ${clipper}, which crops — the copy reaches the frame with a flat ` +
                    `edge there instead of its own letterforms. Its line box fits, so this is the ` +
                    `font's ink hanging outside it: give the cropping box room beyond the ` +
                    `font-size rather than sizing it to the type.${hatch}`,
            );
        }
        return messages;
    },
};

export const CORE_CLIP_LINT_RULES: RuntimeLintRule[] = [
    shapeClippedByViewbox,
    textClippedByContainer,
];
